import React, { useState, useEffect } from 'react';
import { posthogService } from '../../services/PostHogService';
import { X, Shield, Info } from 'lucide-react';

interface PrivacyConsentBannerProps {
  onConsentChange?: (hasConsent: boolean) => void;
}

/**
 * Privacy Consent Banner Component
 * 
 * Displays a GDPR-compliant consent banner for analytics tracking.
 * Uses a clean, accessible design that explains data collection practices
 * and gives users clear options to accept or decline analytics.
 */
const PrivacyConsentBanner: React.FC<PrivacyConsentBannerProps> = ({ 
  onConsentChange 
}) => {
  // Track if banner should be shown (only on first visit or when consent is unknown)
  const [isVisible, setIsVisible] = useState(false);
  // Track if the details section is expanded
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if we already have a stored consent value
    const storedConsent = localStorage.getItem('analytics_consent');
    
    // Only show the banner if consent hasn't been decided yet
    if (storedConsent === null) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    posthogService.setConsent(true);
    setIsVisible(false);
    if (onConsentChange) {
      onConsentChange(true);
    }
  };

  const handleDecline = () => {
    posthogService.setConsent(false);
    setIsVisible(false);
    if (onConsentChange) {
      onConsentChange(false);
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-lg dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
      <div className="container mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
          <div className="flex-1 pr-4 mb-4 md:mb-0">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                <Shield className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Your Privacy Matters
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300" data-component-name="PrivacyConsentBanner">
                  Meduler uses privacy-safe analytics to improve your experience. No personally identifiable information is collected.
                </p>
                
                <button 
                  onClick={toggleDetails}
                  className="mt-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm flex items-center"
                >
                  <Info className="h-4 w-4 mr-1" />
                  {showDetails ? 'Hide details' : 'Learn more about our privacy practices'}
                </button>
                
                {showDetails && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm text-gray-600 dark:text-gray-300">
                    <h4 className="font-medium mb-2 text-gray-900 dark:text-white">How We Protect Your Privacy</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>We generate a random UUID that cannot be traced back to you</li>
                      <li>This ID is encrypted using the Web Crypto API before storage</li>
                      <li>We only collect non-sensitive usage data to improve the app</li>
                      <li>You can change your consent settings anytime in your profile</li>
                      <li>We follow GDPR, HIPAA, and SOC compliance standards</li>
                      <li>No data is shared with third parties outside our analytics service</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 self-end md:self-center">
            <button
              onClick={handleDecline}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Accept
            </button>
          </div>
        </div>
        
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          aria-label="Close privacy banner"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default PrivacyConsentBanner;
